<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { VDataTable } from 'vuetify/components';
import { useConfigHelper } from '@ilteducation/config';
import { CustomerMgmtTypes, SearchOrganizationUserSoryBy, useGetOrganizationGroups } from '@ilteducation/customer-mgmt';
import { useGradeHelpers } from '@ilteducation/grade-helpers-vue';
import type { FetchItems } from '@ilteducation/components';

import MainTable from '../el/MainTable.vue';
import { useLocale } from '../composables/useLocale';
import { useDefaultGroupListRouteQuery } from '../composables/default';
import { Filters } from '../el/table/types';
import { Prettify } from '../types';

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { loadGroups } = useGetOrganizationGroups();
const { param: groupListQuery } = useDefaultGroupListRouteQuery();
const realm = useConfigHelper().getRealm();
const { convertYearsInSchoolToGrade } = useGradeHelpers(realm);

const headers = computed((): VDataTable['$props']['headers'] => [
  { title: t('lcp.name'), key: 'name', sortable: true, nowrap: true, maxWidth: '280px' },
  {
    title: t('lcp.type'),
    key: 'type',
    sortable: false,
    value: (item) => (item.type ? t(`lcp.group_type_${item.type.toLowerCase()}`) : null),
  },
  {
    title: t('lcp.grade'),
    key: 'grade',
    sortable: false,
    value: (item) =>
      item.yearsInSchool === null || item.yearsInSchool === undefined
        ? null
        : convertYearsInSchoolToGrade(item.yearsInSchool),
  },
  { title: t('lcp.members'), key: 'memberCount', sortable: false },
  { title: t('lcp.code'), key: 'code', sortable: false },
]);

const props = defineProps<{
  organizationUid: string;
}>();

const theTable = ref();

const filters: Prettify<Filters<keyof CustomerMgmtTypes.GetGroupsRequest['filters']>> = {};

const itemsPerPage = computed(() => {
  return groupListQuery.value?.pageSize !== undefined && groupListQuery.value?.pageSize !== null
    ? Number(groupListQuery.value.pageSize)
    : undefined;
});

const fetchItems: FetchItems<typeof loadGroups> = async (args) => {
  if (!props.organizationUid) {
    return {
      data: [],
      meta: {
        totalItems: 0,
        pageNumber: args.pageNumber,
        pageSize: args.pageSize,
        sortBy: args.sortBy || 'uid',
        sortOrder: args.sortOrder || 'asc',
      },
    } satisfies CustomerMgmtTypes.GetGroupsResponse;
  }

  return loadGroups(
    props.organizationUid,
    args.pageNumber,
    args.pageSize,
    args.sortBy as SearchOrganizationUserSoryBy,
    args.sortOrder,
    args.q,
    args.filters,
  );
};

defineExpose({
  reloadGroups: async () => {
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 5000));
    await theTable?.value?.reloadItems();
  },
});
</script>

<template>
  <MainTable
    list="groups"
    ref="theTable"
    :headers="headers"
    :itemsPerPage="itemsPerPage"
    :sort-order="groupListQuery?.sortOrder"
    :sort-by="groupListQuery?.sortBy"
    :search-string="groupListQuery?.searchString"
    :fetchItems="fetchItems"
    :organizationUid="organizationUid"
    :filters="filters"
    show-select
    selectStrategy="page"
    :enable-footer-panel="smAndDown"
  >
  </MainTable>
</template>
